import React, { useState } from "react";
import { Button, notification } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import {
  trackAddonLockCtaClicked,
  trackAddonLockViewed,
} from "util/productAnalytics";
import { PRODUCT_LOCK } from "util/productLockConfig";
import styles from "./style.module.scss";

const ProductLockPage = ({
  productKey,
  theme = "dark",
  userId,
  onActivated,
  compact = false,
  children,
}) => {
  const { t } = useTranslation();
  const spec = PRODUCT_LOCK[productKey];
  const [starting, setStarting] = useState(false);
  const [pending, setPending] = useState(false);

  React.useEffect(() => {
    if (productKey) trackAddonLockViewed(productKey);
  }, [productKey]);

  if (!spec) return null;

  const i18n = spec.i18nKey;
  const isFreemium = spec.mode === "freemium";

  const handleStart = async () => {
    if (!userId || !spec.signupKey) return;
    setStarting(true);
    const result = await apiRequest(
      "subscriptions/selectProduct",
      "POST",
      { user_id: userId, product_key: spec.signupKey },
      true
    );
    setStarting(false);
    if (!result || result.error) {
      notification.error({
        message: t("common.error", "Error"),
        description: result?.error || t("signup_page.contact_support"),
      });
      return;
    }
    if (result.pending_approval) {
      setPending(true);
      notification.info({
        message: t("product_lock.pending_title"),
        description: t("product_lock.pending_body"),
      });
      return;
    }
    if (result.self_serve && onActivated) {
      onActivated(result);
      return;
    }
    notification.success({
      message: t("common.success", "Success"),
      description: t("product_lock.sales_requested"),
    });
  };

  return (
    <div
      className={`${styles.lockPage} ${
        theme === "dark" ? styles.lockPageDark : styles.lockPageLight
      }${compact ? ` ${styles.lockPageCompact}` : ""}`}
    >
      <div className={styles.lockBackground}>
        <div className={styles.lockGrid} />
        <div className={styles.lockOrbOne} />
        <div className={styles.lockOrbTwo} />
        {children}
      </div>
      <div className={styles.lockModal}>
        <div className={styles.lockIcon}>
          <ThunderboltOutlined />
        </div>
        <p className={styles.lockEyebrow}>{t(`${i18n}.eyebrow`)}</p>
        <h1>{t(`${i18n}.heading`)}</h1>
        <p className={styles.lockText}>{t(`${i18n}.body`)}</p>
        {pending ? (
          <p className={styles.lockPending}>{t("product_lock.pending_body")}</p>
        ) : (
          <div className={styles.lockActions}>
            {isFreemium ? (
              <Button
                type="primary"
                size="large"
                loading={starting}
                onClick={handleStart}
                className={styles.lockCtaPrimary}
              >
                {t("product_lock.start")}
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                href={`mailto:hello@pivony.com?subject=${spec.mailSubject}`}
                className={styles.lockCtaPrimary}
                onClick={() => trackAddonLockCtaClicked(productKey, "talk_to_sales")}
              >
                {t("product_lock.talk_to_sales")}
              </Button>
            )}
            {isFreemium ? (
              <Button
                size="large"
                href={`mailto:hello@pivony.com?subject=${spec.mailSubject}`}
                className={styles.lockCtaSecondary}
                onClick={() => trackAddonLockCtaClicked(productKey, "talk_to_sales")}
              >
                {t("product_lock.talk_to_sales")}
              </Button>
            ) : (
              <Button
                size="large"
                href={spec.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.lockCtaSecondary}
                onClick={() => trackAddonLockCtaClicked(productKey, "upgrade_demo")}
              >
                {t("product_lock.upgrade_demo")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductLockPage;
